import apiClient from './apiClient';
import type { CreateSampleRequest, Sample, SampleWithDetails } from '../types/location';

export const getAllSamples = async (): Promise<SampleWithDetails[]> => {
  const response = await apiClient.get<SampleWithDetails[]>('/samples/all');
  return response.data;
};

export const getSamplesByLocation = async (
  locationKey: string,
): Promise<Sample[]> => {
  const response = await apiClient.get<Sample[]>(
    `/samples/location/${locationKey}`,
  );
  return response.data;
};

export const createSample = async (
  locationKey: string,
  data: CreateSampleRequest,
): Promise<Sample> => {
  const response = await apiClient.post<Sample>(
    `/samples/location/${locationKey}`,
    data,
  );
  return response.data;
};

export const updateSample = async (
  id: string,
  data: Partial<CreateSampleRequest>,
): Promise<Sample> => {
  const response = await apiClient.put<Sample>(`/samples/${id}`, data);
  return response.data;
};

export const deleteSample = async (
  id: string,
  opts?: { cascade?: boolean },
): Promise<void> => {
  // P2-10: cascade=true is sent only after the user confirmed deleting the
  // sample's tests too (409 SAMPLE_HAS_TESTS).
  const url = opts?.cascade ? `/samples/${id}?cascade=true` : `/samples/${id}`;
  await apiClient.delete(url);
};

/** P2-10: one sample the backend refused to delete because it still has tests. */
export interface SampleDeleteBlocked {
  sampleKey: string;
  /** Active WorkOrderDetail rows (tests) referencing the sample. */
  testCount: number;
  /** Active ScheduleTasks referencing those tests (booked/scheduled work). */
  scheduledTaskCount: number;
  /** The backend's human message. */
  message: string;
}

export interface DeleteSamplesResult {
  /** Sample keys actually deleted. */
  deleted: string[];
  /** P2-10: samples refused with 409 SAMPLE_HAS_TESTS — confirm + retry with cascade. */
  blocked: SampleDeleteBlocked[];
}

/**
 * P2-10: deletes samples individually and AGGREGATES the 409 SAMPLE_HAS_TESTS
 * refusals instead of failing the whole batch. Callers show one confirm for
 * the blocked set and retry those keys with { cascade: true }. Any other
 * error still rejects.
 */
export const deleteSamples = async (
  ids: string[],
  opts?: { cascade?: boolean },
): Promise<DeleteSamplesResult> => {
  const deleted: string[] = [];
  const blocked: SampleDeleteBlocked[] = [];
  for (const id of ids) {
    try {
      await deleteSample(id, opts);
      deleted.push(id);
    } catch (err) {
      const resp = (err as {
        response?: {
          status?: number;
          data?: { code?: string; error?: string; testCount?: number; scheduledTaskCount?: number };
        };
      }).response;
      if (resp?.status === 409 && resp.data?.code === 'SAMPLE_HAS_TESTS') {
        blocked.push({
          sampleKey: id,
          testCount: resp.data.testCount ?? 0,
          scheduledTaskCount: resp.data.scheduledTaskCount ?? 0,
          message: resp.data.error ?? 'Sample has tests on work orders.',
        });
        continue;
      }
      throw err;
    }
  }
  return { deleted, blocked };
};

/**
 * P2-10: shared confirm-dialog copy for the blocked-sample cascade (used by
 * LocationsPage and LocationsTabContent — one builder so the wording can't
 * drift). The caller wires onConfirm to `deleteSamples(keys, { cascade: true })`.
 */
export const buildSampleCascadeCopy = (
  blocked: SampleDeleteBlocked[],
): { title: string; message: string; confirmLabel: string; scheduledCount: number } => {
  const testCount = blocked.reduce((n, b) => n + b.testCount, 0);
  const scheduledCount = blocked.reduce((n, b) => n + b.scheduledTaskCount, 0);
  const schedNote =
    scheduledCount > 0
      ? ` ${scheduledCount} of those tests are on the schedule — it will read out of sync until you run "Sync now" on the work order.`
      : '';
  return {
    title: 'Samples have tests on work orders',
    message:
      `${blocked.length} sample(s) still have ${testCount} test(s) on work orders. ` +
      `Deleting the sample(s) also removes those tests.${schedNote} This cannot be undone.`,
    confirmLabel: 'Delete samples + tests',
    scheduledCount,
  };
};

export interface BulkCreateResult {
  imported: number;
  failed: number;
  errors: { row: number; message: string }[];
  samples: Sample[];
}

export const bulkUpdateField = async (
  sampleKeys: string[],
  field: string,
  value: string | null,
): Promise<{ updated: number }> => {
  const response = await apiClient.put<{ updated: number }>(
    '/samples/bulk-update-field',
    { sampleKeys, field, value },
  );
  return response.data;
};

export const bulkCreateSamples = async (
  locationKey: string,
  samples: CreateSampleRequest[],
): Promise<BulkCreateResult> => {
  const response = await apiClient.post<BulkCreateResult>(
    `/samples/location/${locationKey}/bulk`,
    samples,
  );
  return response.data;
};
